import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { Mic, PackageSearch, TriangleAlert } from 'lucide-react';
import { Badge, Button, Card, TextArea } from '@tcrd/ui';
import { useToast } from '../../components/toast';
import { api, errorMessage } from '../../lib/api';
import { fmtDateTime } from '../../lib/format';
import { deleteRecording, getRecording } from '../../lib/trip-recorder';
import type { IncidentDto, RideDto } from '../../lib/types';

export const INCIDENT_LABEL: Record<IncidentDto['type'], string> = {
  SOS: 'Emergencia',
  RIDECHECK: '¿Todo bien?',
  SAFETY: 'Reporte de seguridad',
  LOST_ITEM: 'Objeto perdido',
};

const FINISHED = ['COMPLETED', 'CANCELLED'];

/** Reportes del viaje: problema de seguridad (con la grabación si existe) y objetos perdidos. */
export function RideIncidents({ ride, role }: { ride: RideDto; role: 'customer' | 'driver' }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { search } = useLocation();
  const q = useQuery({ queryKey: ['incidents', ride.id], queryFn: () => api<IncidentDto[]>(`/rides/${ride.id}/incidents`) });
  const [form, setForm] = useState<'SAFETY' | 'LOST_ITEM' | null>(new URLSearchParams(search).get('report') ? 'SAFETY' : null);
  const [text, setText] = useState('');
  const [recording, setRecording] = useState<{ blob: Blob; seconds: number } | null>(null);
  const [attach, setAttach] = useState(true);
  const [busy, setBusy] = useState(false);
  const [answers, setAnswers] = useState<Record<string, string>>({});

  useEffect(() => {
    void getRecording(ride.id).then((r) => setRecording(r ? { blob: r.blob, seconds: Math.round((r.endedAt - r.startedAt) / 1000) } : null));
  }, [ride.id, form]);

  const refresh = () => qc.invalidateQueries({ queryKey: ['incidents', ride.id] });
  const submit = async () => {
    if (!form || text.trim().length < 3) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('type', form);
      fd.append('description', text.trim());
      const withAudio = form === 'SAFETY' && attach && recording;
      if (withAudio) fd.append('file', recording.blob, `viaje.${recording.blob.type.includes('mp4') ? 'm4a' : recording.blob.type.includes('ogg') ? 'ogg' : 'webm'}`);
      await api(`/rides/${ride.id}/incidents`, { method: 'POST', form: fd });
      // La copia local ya no hace falta una vez enviada.
      if (withAudio) await deleteRecording(ride.id);
      toast(form === 'LOST_ITEM' ? 'Avisamos a tu conductor.' : 'Reporte enviado. La administración lo revisará.', 'success');
      setForm(null);
      setText('');
      await refresh();
    } catch (e) {
      toast(errorMessage(e), 'danger');
    } finally {
      setBusy(false);
    }
  };

  const resolve = async (id: string) => {
    const resolution = answers[id]?.trim();
    if (!resolution) return;
    try {
      await api(`/incidents/${id}/resolve`, { method: 'PATCH', body: { resolution } });
      toast('Respuesta enviada al cliente', 'success');
      await refresh();
    } catch (e) {
      toast(errorMessage(e), 'danger');
    }
  };

  const items = q.data ?? [];
  return (
    <div className="flex flex-col gap-3" id="reportes">
      {items.map((i) => (
        <Card key={i.id} className="p-4 flex flex-col gap-2 text-sm">
          <div className="flex items-center justify-between gap-2">
            <p className="font-semibold">{INCIDENT_LABEL[i.type]}</p>
            <Badge tone={i.status === 'OPEN' ? 'warning' : 'success'}>{i.status === 'OPEN' ? 'Abierto' : 'Resuelto'}</Badge>
          </div>
          {i.description && <p data-no-translate>{i.description}</p>}
          {i.audioUrl && <audio controls src={i.audioUrl} className="w-full" />}
          <p className="text-xs text-[var(--text-muted)]">{fmtDateTime(i.createdAt)}</p>
          {i.resolution && <p className="rounded-xl bg-[var(--surface-2)] p-3" data-no-translate>{i.resolution}</p>}
          {role === 'driver' && i.type === 'LOST_ITEM' && i.status === 'OPEN' && (
            <div className="flex flex-col gap-2">
              <TextArea label="Respuesta al cliente" placeholder="Ej.: La tengo, te la llevo mañana a las 10 am." value={answers[i.id] ?? ''} onChange={(e) => setAnswers({ ...answers, [i.id]: e.target.value })} maxLength={500} />
              <Button size="sm" onClick={() => void resolve(i.id)} disabled={!answers[i.id]?.trim()}>Responder y cerrar</Button>
            </div>
          )}
        </Card>
      ))}

      {form ? (
        <Card className="p-4 flex flex-col gap-3">
          <p className="font-semibold">{form === 'LOST_ITEM' ? '¿Qué olvidaste?' : 'Reportar un problema de seguridad'}</p>
          <TextArea
            label={form === 'LOST_ITEM' ? 'Describe el objeto' : 'Cuéntanos qué pasó'}
            placeholder={form === 'LOST_ITEM' ? 'Ej.: Una cartera negra en el asiento trasero' : 'Describe la situación con el mayor detalle posible'}
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={1000}
            autoFocus
          />
          {form === 'SAFETY' && recording && (
            <label className="tap flex items-center gap-3 rounded-2xl border border-[var(--border)] px-4 h-12 text-sm">
              <Mic className="size-4" aria-hidden />
              <span className="flex-1">Adjuntar la grabación del viaje ({Math.max(1, Math.round(recording.seconds / 60))} min)</span>
              <input type="checkbox" checked={attach} onChange={(e) => setAttach(e.target.checked)} className="size-5 accent-[#E5252A]" />
            </label>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Button variant="secondary" onClick={() => setForm(null)}>Cancelar</Button>
            <Button variant="accent" loading={busy} disabled={text.trim().length < 3} onClick={() => void submit()}>Enviar</Button>
          </div>
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          {role === 'customer' && FINISHED.includes(ride.status) && (
            <Button variant="secondary" block icon={<PackageSearch className="size-5" />} onClick={() => setForm('LOST_ITEM')}>Olvidé un objeto en el vehículo</Button>
          )}
          <Button variant="ghost" block icon={<TriangleAlert className="size-5" />} onClick={() => setForm('SAFETY')}>Reportar un problema de seguridad</Button>
        </div>
      )}
    </div>
  );
}
